import React, { useContext, useEffect, useState } from 'react'
import { of } from 'rxjs'
import { DraggableLayout, IDraggableZone, getDragResultDelta, makeStyles, updateAfterDragResult, useObservable } from '@cicero/ui'
import { AttributeSchema, AttributeValueSchema, PageDoc, PageSchema, StoreContext, useModel } from 'models'
import { IPageFilter } from 'models/filters'
import { PageCard } from 'views'
import { PageQuickCreateForm } from 'forms'
import { EditorContext, IDashboardWidgetElement, IEditorWidget } from '../EditorService'
import { useAttributesInOrder } from 'models/hooks'

export const GroupedList = ({
  config: {
    empty_group_name,
    display_attribute_ids,
    filter_template_ids,
    filters = [],
    group_by_attribute_id,
    group_by_filters = [],
  },
}: {
  config: IGroupedListWidgetConifg
}) => {
  const { containerLoading, glistPlacementCell, glistGroup } = useStyles()
  const store = useContext(StoreContext)
  const editor = useContext(EditorContext)
  const Attributes = useModel(AttributeSchema)
  const AttributeValues = useModel(AttributeValueSchema)
  const [zones, setZones] = useState<IGroupedListZone[][]>([])
  const [isLoading, results] = PageSchema.HOOKS.usePageFilters(filters)
  const attribute = useObservable(() => group_by_attribute_id ? Attributes.GET$(group_by_attribute_id) : of(null), null, [group_by_attribute_id])
  const groups = PageSchema.HOOKS.usePagesGroupedByAttribute(results, attribute)
  const options = AttributeSchema.HOOKS.useAttributeOptions(attribute, group_by_filters)
  const displayAttributes = useAttributesInOrder(display_attribute_ids)

  useEffect(() => {
    const z: IGroupedListZone[][] = [
      ...options.map(o => [{ id: o.id, title: o.value?.title || o.value, option: o as any, parent: o.value, cards: groups[o.id] || [] }]),
      [{ id: '_blank', title: empty_group_name || 'No Value', option: null, parent: null, cards: groups['_blank'] || [] }],
    ]

    setZones(z)
    // LOGGER.debug('got results', { attribute: attribute?.label, results, groups, options, z })
  }, [results, groups, options])

  return <DraggableLayout
    zones={zones}
    classes={{ container: isLoading ? containerLoading : undefined, placementCell: glistPlacementCell, placementList: glistGroup }}
    renderPlacementHeader={zone => <h3>{zone.title}</h3>}
    renderPlacementFooter={(zone) => <PageQuickCreateForm
      buttonText="+ Add"
      template_id={filter_template_ids ? filter_template_ids[0] : undefined}
      parent_id={editor.page ? editor.page.id : undefined}
      afterCreate={(page) => {
        if (!attribute || zone.id === 'blank') { return }
        AttributeSchema.HOOKS.persistAttributeValueChanges(store, page, attribute, [], [zone.id])
      }}
    />}
    renderCard={(page: PageDoc) => <PageCard
      page={page}
      attributes={displayAttributes}
      attributesInline
      quickOpen
      strikeArchived
    />}
    onDragEnd={async r => {
      const { sourceAddress, targetAddress, sourceZone, sourceCard, targetZone } = getDragResultDelta<IGroupedListZone>(r, zones)
      if (sourceAddress && targetAddress && sourceCard && sourceZone && targetZone && group_by_attribute_id && attribute) {
        const { parent } = targetZone
        setZones(updateAfterDragResult(zones, { card: sourceCard, sourceAddress, targetAddress }))
        const currentValues = await AttributeValues.QUERY({ selector: { page_id: sourceCard?.id, attribute_id: group_by_attribute_id } })
        await AttributeSchema.HOOKS.persistAttributeValueChanges(store, sourceCard, attribute, currentValues, parent ? [parent] : [])
      }
    }}
  />
}

const useStyles = makeStyles({
  containerLoading: {},
  glistPlacementCell: {
    borderBottom: '1px solid #FEFEFE',
    borderRadius: 4,
    padding: 16,
  },
  glistGroup: {
    width: '100%',
    padding: 6,
  },
})

export const groupedListWidget: IEditorWidget = {
  id: 'groupedlist',
  name: 'Grouped List',
  description: 'Show a list of pages, grouped by a given attribute',
  image: require('./img/groupedlist.png'),
  renderer: (leaf) => <GroupedList config={leaf} />,
}

export interface IGroupedListWidgetConifg extends IDashboardWidgetElement {
  filters?: IPageFilter[]
  display_attribute_ids?: string[]
  group_by_filters?: IPageFilter[]
  group_by_attribute_id?: string
  group_by_template_ids?: string[]
  empty_group_name?: string
  filter_template_ids?: string[]
}

export interface IGroupedListZone extends IDraggableZone {
  parent: PageDoc|null
  cards?: PageDoc[]
}
